import React from "react";
import { Checkbox, Group, Paper, SimpleGrid, Stack, Text, Tooltip } from "@mantine/core";
import { useTranslation } from "react-i18next";
import type { RoleModule } from "@models/access";

interface PermissionPickerProps {
  /** Every permission of the application, by module (`assigned` is ignored: `selected` decides). */
  modules: RoleModule[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  /** Keys the actor may not grant: shown, never toggled. */
  locked: (key: string) => boolean;
  readOnly?: boolean;
}

/** Permissions grouped by module as checkboxes, with name and key, and "select all in module". */
const PermissionPicker: React.FC<PermissionPickerProps> = ({ modules, selected, onChange, locked, readOnly }) => {
  const { t } = useTranslation();

  const toggle = (keys: string[], on: boolean) => {
    const next = new Set(selected);
    keys.forEach((k) => (on ? next.add(k) : next.delete(k)));
    onChange(next);
  };

  return (
    <Stack gap="sm">
      {modules.map((module) => {
        const editable = module.permissions.map((p) => p.key).filter((k) => !locked(k));
        const all = editable.length > 0 && editable.every((k) => selected.has(k));
        const some = editable.some((k) => selected.has(k));
        return (
          <Paper key={module.name} withBorder p="sm" radius="sm">
            <Stack gap="xs">
              <Group justify="space-between">
                <Text fw={600} size="sm">{module.name}</Text>
                {!readOnly && (
                  <Checkbox
                    size="xs"
                    label={t("admin.access.roles.editor.selectAll")}
                    checked={all}
                    indeterminate={some && !all}
                    disabled={editable.length === 0}
                    onChange={(e) => toggle(editable, e.currentTarget.checked)}
                  />
                )}
              </Group>
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xs">
                {module.permissions.map((permission) => {
                  const isLocked = locked(permission.key);
                  const box = (
                    <Checkbox
                      key={permission.key}
                      checked={selected.has(permission.key)}
                      disabled={readOnly || isLocked}
                      onChange={(e) => toggle([permission.key], e.currentTarget.checked)}
                      label={
                        <Stack gap={0}>
                          <Text size="sm">{permission.name}</Text>
                          <Text size="xs" c="dimmed" ff="monospace">{permission.key}</Text>
                        </Stack>
                      }
                    />
                  );
                  return isLocked && !readOnly ? (
                    <Tooltip key={permission.key} label={t("admin.access.roles.editor.notHeld")}>
                      <div>{box}</div>
                    </Tooltip>
                  ) : box;
                })}
              </SimpleGrid>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
};

export default PermissionPicker;
